import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardList,
  Clock,
  Calendar,
  Star,
  ArrowRight,
  Check,
  FileText,
  ChevronRight,
  X,
  AlertCircle,
  ChevronLeft,
  CheckCircle,
  Filter,
  ChartBar,
  Search,
  HelpCircle,
  Info,
  Upload,
  Calendar as CalendarIcon,
  Hash,
  Mail,
  Download,
  Plus,
  ChevronDown,
  Settings,
  Copy,
  Trash2,
  Menu,
  BookOpen,
  Users,
  Building2,
  GraduationCap,
  Target,
  Activity,
  Award,
  UserPlus,
  ScrollText,
} from "lucide-react";
import { format } from "date-fns";
import confetti from 'canvas-confetti';

// Helper Components
const LoadingSpinner = () => (
  <motion.div
    animate={{ rotate: 360 }}
    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
    className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"
  />
);

const NoDataMessage = ({ message, icon: Icon = HelpCircle }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <Icon className="w-16 h-16 text-gray-400 mb-4" />
    <h3 className="text-xl font-medium text-gray-900 mb-2">{message}</h3>
    <p className="text-gray-500 max-w-md">
      Check back later for new surveys or explore other ways to engage with your
      alma mater.
    </p>
  </div>
);





// Mouse light effect
const MouseLight = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);

  return (
    <div 
      className="absolute inset-0 overflow-hidden pointer-events-none"
      onMouseMove={handleMouseMove}
    >
      <div
        className="absolute -inset-px transition-all duration-200 ease-out"
        style={{
          background: `radial-gradient(800px circle at ${mousePosition.x}px ${mousePosition.y}px, 
            rgba(139, 92, 246, 0.07),
            rgba(99, 102, 241, 0.07),
            transparent 40%)`
        }}
      />
    </div>
  );
};

const HeroInfoSection = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      icon: Target,
      title: "NEP 2020 Integration",
      description: "Shape the future of education by aligning curriculum with industry needs",
      color: "from-violet-500 to-purple-600",
      bgAccent: "bg-violet-100",
      textAccent: "text-violet-600"
    },
    {
      icon: BookOpen,
      title: "Academic Excellence",
      description: "Contribute to course refinement and teaching methodologies",
      color: "from-blue-500 to-indigo-600",
      bgAccent: "bg-blue-100",
      textAccent: "text-blue-600"
    },
    {
      icon: ChartBar,
      title: "Industry Insights",
      description: "Bridge the gap between academia and professional requirements",
      color: "from-emerald-500 to-teal-600",
      bgAccent: "bg-emerald-100",
      textAccent: "text-emerald-600"
    }
  ];

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-[#1a1437] to-gray-900">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Geometric patterns */}
        <div className="absolute inset-0">
          <svg className="w-full h-full opacity-[0.15]">
            <defs>
              <pattern id="grid-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
              </pattern>
              <pattern id="dot-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)" />
            <rect width="100%" height="100%" fill="url(#dot-pattern)" />
          </svg>
        </div>

        {/* Animated glowing orbs */}
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className={`absolute rounded-full blur-2xl
              ${i % 3 === 0 ? 'bg-purple-400 w-96 h-96 opacity-20' : 
                i % 3 === 1 ? 'bg-blue-400 w-[30rem] h-[30rem] opacity-15' : 
                'bg-violet-400 w-[28rem] h-[28rem] opacity-20'}
              animate-float-slow`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          />
        ))}
      </div>

      {/* Mouse light effect */}
      <MouseLight />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center px-4 py-1.5 rounded-full 
              bg-white/10 backdrop-blur-sm border border-white/20 mb-6"
          >
            <GraduationCap className="w-5 h-5 text-purple-300 mr-2" />
            <span className="text-sm font-medium text-purple-200">
              Alumni Feedback Portal
            </span>
          </motion.div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8
            bg-clip-text text-transparent bg-gradient-to-r 
            from-purple-200 via-indigo-200 to-blue-200"
          >
            Shape Tomorrow's Education
          </h1>

          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-12">
            Your insights drive educational excellence. Help us evolve our curriculum
            and enhance student outcomes through your valuable feedback.
          </p>
        </motion.div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onMouseEnter={() => setActiveFeature(index)}
                className="group relative overflow-hidden rounded-2xl bg-white/[0.05] 
                  backdrop-blur-lg border border-white/10 p-6 hover:bg-white/[0.08]
                  transition-all duration-300"
              >
                <motion.div
                  className={`w-12 h-12 rounded-xl ${feature.bgAccent}
                    flex items-center justify-center mb-4
                    group-hover:scale-110 transition-transform duration-300`}
                >
                  <Icon className={`w-6 h-6 ${feature.textAccent}`} />
                </motion.div>

                <h3 className="text-xl font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                
                <p className="text-gray-300 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex justify-center mt-12"
        >
          <button 
            onClick={() => document.getElementById('surveysection').scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center px-6 py-3 rounded-xl
              bg-gradient-to-r from-purple-500 to-indigo-600 text-white
              hover:from-purple-600 hover:to-indigo-700 transition-all
              font-medium shadow-lg shadow-purple-500/25 hover:shadow-xl
              hover:shadow-purple-500/30 hover:-translate-y-0.5"
          >
            Take Survey
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>
        </motion.div>
      </div>

      {/* Bottom curve */}
      <div className="absolute bottom-0 inset-x-0">
        <svg viewBox="0 0 1440 74" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 24C480 74 960 74 1440 24V74H0V24Z" fill="rgb(249 250 251)" />
        </svg>
      </div>
    </div>
  );
};

// Required animations in your global CSS
const styles = `
  @keyframes float-slow {
    0%, 100% { transform: translateY(0) rotate(0); }
    50% { transform: translateY(-20px) rotate(5deg); }
  }

  .animate-float-slow {
    animation: float-slow 20s ease-in-out infinite;
  }
`;









// Survey Status Badge Component
const StatusBadge = ({ status, dueDate }) => {
  const getStatusConfig = () => {
    const now = new Date();
    const due = new Date(dueDate);
    const daysLeft = Math.ceil((due - now) / (1000 * 60 * 60 * 24));

    if (status === "completed") {
      return {
        label: "Completed",
        icon: CheckCircle,
        color: "text-green-600 bg-green-50 border-green-200",
      };
    }

    if (now > due) {
      return {
        label: "Expired",
        icon: Clock,
        color: "text-red-600 bg-red-50 border-red-200",
      };
    }

    if (daysLeft <= 3) {
      return {
        label: `${daysLeft} day${daysLeft !== 1 ? "s" : ""} left`,
        icon: Clock,
        color: "text-orange-600 bg-orange-50 border-orange-200",
      };
    }

    return {
      label: "Active",
      icon: CheckCircle,
      color: "text-blue-600 bg-blue-50 border-blue-200",
    };
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
      border ${config.color} transition-colors duration-200`}
    >
      <Icon className="w-4 h-4 mr-1.5" />
      {config.label}
    </span>
  );
};

// Survey Card Component
const SurveyCard = ({ survey, isSubmitted, onStart }) => {
  const theme = survey.settings?.theme || "#2563eb";

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group"
    >
      {/* Color Bar Based on Theme */}
      <div
        className="absolute top-0 left-0 right-0 h-1 transition-all duration-200"
        style={{ backgroundColor: theme }}
      />

      {survey.surveyImage && (
        <div className="relative h-48 overflow-hidden">
          <img
            src={survey.surveyImage}
            alt={survey.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        </div>
      )}

      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <StatusBadge
            status={isSubmitted ? "completed" : "active"}
            dueDate={survey.schedule.endDate}
          />
          {survey.target.batch?.length > 0 && (
            <div className="flex items-center text-sm text-gray-600">
              <GraduationCap className="w-4 h-4 mr-1.5" />
              {survey.target.batch.join(", ")}
            </div>
          )}
        </div>

        <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
          {survey.title}
        </h3>

        <p className="text-gray-600 mb-6 text-sm line-clamp-2">
          {survey.description}
        </p>

        <div className="flex items-center justify-between pt-6 border-t border-gray-100">
          <div className="flex flex-col">
            <span className="text-xs text-gray-500">Due Date</span>
            <span className="text-sm font-medium text-gray-900">
              {format(new Date(survey.schedule.endDate), "MMM d, yyyy")}
            </span>
          </div>

          {isSubmitted ? (
            <div className="flex items-center text-green-600">
              <CheckCircle className="w-5 h-5 mr-2" />
              <span className="text-sm font-medium">Completed</span>
            </div>
          ) : (
            <button
              onClick={() => onStart(survey)}
              disabled={new Date(survey.schedule.endDate) < new Date()}
              style={{ backgroundColor: theme }}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium
                transition-colors duration-200 text-white hover:opacity-90 
                disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed`}
            >
              {new Date(survey.schedule.endDate) < new Date()
                ? "Expired"
                : "Start Survey"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Filter Tabs Component
const FilterTabs = ({ activeFilter, onFilterChange }) => {
  const filters = [
    { id: "all", label: "All Surveys", icon: ClipboardList },
    { id: "active", label: "Active", icon: Activity },
    { id: "completed", label: "Completed", icon: CheckCircle },
  ];

  return (
    <div className="flex space-x-2 overflow-x-auto pb-2">
      {filters.map((filter) => {
        const Icon = filter.icon;
        return (
          <button
            key={filter.id}
            onClick={() => onFilterChange(filter.id)}
            className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium
              transition-colors duration-200 whitespace-nowrap
              ${
                activeFilter === filter.id
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
          >
            <Icon className="w-4 h-4 mr-2" />
            {filter.label}
          </button>
        );
      })}
    </div>
  );
};

// Stats Section Component
const StatsSection = ({ surveys, submittedCount }) => {
  const stats = [
    {
      label: "Available Surveys",
      value: surveys.length,
      icon: ClipboardList,
      color: "bg-blue-100 text-blue-600",
    },
    {
      label: "Completed",
      value: submittedCount,
      icon: CheckCircle,
      color: "bg-green-100 text-green-600",
    },
    {
      label: "Active Surveys",
      value: surveys.filter((s) => new Date(s.schedule.endDate) > new Date())
        .length,
      icon: Activity,
      color: "bg-purple-100 text-purple-600",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <div
            key={index}
            className="bg-white rounded-xl p-6 border border-gray-200"
          >
            <div className="flex items-center">
              <div
                className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}
              >
                <Icon className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <h4 className="text-2xl font-bold text-gray-900">
                  {stat.value}
                </h4>
                <p className="text-sm text-gray-600">{stat.label}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Search and Sort Component
const SearchAndSort = ({
  searchTerm,
  onSearchChange,
  sortOrder,
  onSortChange,
}) => (
  <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4 mb-6">
    <div className="flex-1">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Search surveys by title or description..."
          value={searchTerm}
          onChange={onSearchChange}
          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg
            focus:ring-2 focus:ring-blue-500 focus:border-transparent
            placeholder-gray-400 text-gray-900"
        />
      </div>
    </div>

    <select
      value={sortOrder}
      onChange={onSortChange}
      className="px-4 py-3 border border-gray-200 rounded-lg
        focus:ring-2 focus:ring-blue-500 focus:border-transparent
        text-gray-700 bg-white cursor-pointer"
    >
      <option value="deadline">Sort by Deadline</option>
      <option value="recent">Sort by Recent</option>
      <option value="title">Sort by Title</option>
    </select>
  </div>
);
// Question Renderer Component
const QuestionRenderer = ({ question, value, onChange, error }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [question._id]);

  const renderInput = (props) => (
    <input
      ref={inputRef}
      {...props}
      className={`w-full px-4 py-3 text-lg border rounded-lg transition-colors
        focus:outline-none focus:ring-2 
        ${
          error
            ? "border-red-300 focus:ring-red-500 bg-red-50"
            : "border-gray-300 focus:ring-blue-500"
        }`}
    />
  );

  const renderError = () =>
    error && (
      <p className="mt-2 text-sm text-red-600 flex items-center">
        <AlertCircle className="w-4 h-4 mr-1" />
        {error}
      </p>
    );

  switch (question.type) {
    case "short_text":
      return (
        <div className="space-y-2">
          {renderInput({
            type: "text",
            value: value?.answerText || "",
            onChange: (e) => onChange({ answerText: e.target.value }),
            placeholder: "Type your answer here...",
          })}
          {renderError()}
        </div>
      );

    case "long_text":
      return (
        <div className="space-y-2">
          <textarea
            ref={inputRef}
            value={value?.answerText || ""}
            onChange={(e) => onChange({ answerText: e.target.value })}
            placeholder="Type your detailed answer here..."
            rows={4}
            className={`w-full px-4 py-3 text-lg border rounded-lg resize-none
              focus:outline-none focus:ring-2
              ${
                error
                  ? "border-red-300 focus:ring-red-500 bg-red-50"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
          />
          {renderError()}
        </div>
      );

    case "single_choice":
      return (
        <div className="space-y-3">
          {question.options.map((option, index) => (
            <motion.label
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`flex items-center p-4 rounded-lg border-2 cursor-pointer
                transition-colors duration-200
                ${
                  value?.answerText === option
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-blue-200 hover:bg-blue-50/50"
                }`}
            >
              <input
                type="radio"
                checked={value?.answerText === option}
                onChange={() => onChange({ answerText: option })}
                className="w-4 h-4 text-blue-600"
              />
              <span className="ml-3 text-lg">{option}</span>
            </motion.label>
          ))}
          {renderError()}
        </div>
      );

    case "multiple_choice":
      return (
        <div className="space-y-3">
          {question.options.map((option, index) => (
            <motion.label
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`flex items-center p-4 rounded-lg border-2 cursor-pointer
                transition-colors duration-200
                ${
                  (value?.selectedOptions || []).includes(option)
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-blue-200 hover:bg-blue-50/50"
                }`}
            >
              <input
                type="checkbox"
                checked={(value?.selectedOptions || []).includes(option)}
                onChange={(e) => {
                  const currentSelection = value?.selectedOptions || [];
                  const newSelection = e.target.checked
                    ? [...currentSelection, option]
                    : currentSelection.filter((item) => item !== option);
                  onChange({ selectedOptions: newSelection });
                }}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="ml-3 text-lg">{option}</span>
            </motion.label>
          ))}
          {renderError()}
        </div>
      );

    case "linear_scale":
    case "rating":
      const min = question.settings?.minValue || 1;
      const max = question.settings?.maxValue || 5;

      return (
        <div className="space-y-4">
          <div className="flex items-center justify-center space-x-4">
            {Array.from({ length: max - min + 1 }, (_, i) => i + min).map(
              (num) => (
                <motion.button
                  key={num}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => onChange({ scaleValue: num })}
                  className={`w-12 h-12 rounded-lg flex items-center justify-center
                  text-lg font-medium transition-all duration-200
                  ${
                    value?.scaleValue === num
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-blue-50 text-gray-700"
                  }`}
                >
                  {question.type === "rating" ? (
                    <Star
                      className={value?.scaleValue >= num ? "fill-current" : ""}
                      size={20}
                    />
                  ) : (
                    num
                  )}
                </motion.button>
              )
            )}
          </div>
          {renderError()}
        </div>
      );

    case 'rating_grid':
      return (
        <div className="space-y-4">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 text-left"></th>
                {question.settings?.gridColumns?.map((col, colIndex) => (
                  <th key={colIndex} className="px-4 py-2 text-center">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {question.settings?.gridRows?.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-t">
                  <td className="px-4 py-3 font-medium">{row}</td>
                  {question.settings?.gridColumns?.map((col, colIndex) => (
                    <td key={colIndex} className="px-4 py-3 text-center">
                      <div className="flex justify-center space-x-1">
                        {Array.from({ length: question.settings?.maxRating || 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className={`w-5 h-5 cursor-pointer transition-colors ${
                              (value?.gridAnswers || []).find(a => 
                                a.row === row && 
                                a.column === col && 
                                a.rating > i
                              ) ? 'text-yellow-400 fill-current' : 'text-gray-300'
                            }`}
                            onClick={() => {
                              const newGridAnswers = [...(value?.gridAnswers || [])];
                              const existingIndex = newGridAnswers.findIndex(
                                a => a.row === row && a.column === col
                              );
                              if (existingIndex >= 0) {
                                newGridAnswers[existingIndex].rating = i + 1;
                              } else {
                                newGridAnswers.push({ row, column: col, rating: i + 1 });
                              }
                              onChange({ gridAnswers: newGridAnswers });
                            }}
                          />
                        ))}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {renderError()}
        </div>
      );

    case 'numeric_grid':
      return (
        <div className="space-y-4">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 text-left"></th>
                {question.settings?.gridColumns?.map((col, colIndex) => (
                  <th key={colIndex} className="px-4 py-2 text-center">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {question.settings?.gridRows?.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-t">
                  <td className="px-4 py-3 font-medium">{row}</td>
                  {question.settings?.gridColumns?.map((col, colIndex) => (
                    <td key={colIndex} className="px-4 py-3 text-center">
                      <input
                        type="number"
                        className="w-20 px-2 py-1 text-center border rounded-lg focus:ring-2 focus:ring-blue-500"
                        value={(value?.gridAnswers || []).find(
                          a => a.row === row && a.column === col
                        )?.value || ''}
                        onChange={(e) => {
                          const newGridAnswers = [...(value?.gridAnswers || [])];
                          const existingIndex = newGridAnswers.findIndex(
                            a => a.row === row && a.column === col
                          );
                          if (existingIndex >= 0) {
                            newGridAnswers[existingIndex].value = e.target.value;
                          } else {
                            newGridAnswers.push({ row, column: col, value: e.target.value });
                          }
                          onChange({ gridAnswers: newGridAnswers });
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {renderError()}
        </div>
      );


    default:
      return (
        <div className="p-4 bg-gray-50 rounded-lg text-gray-500 flex items-center justify-center">
          <Info className="w-5 h-5 mr-2" />
          Unsupported question type: {question.type}
        </div>
      );
  }
};

// Survey Response Form
const SurveyResponseForm = ({ survey, onSubmit, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const startTimeRef = useRef(new Date());

  const validateAnswer = (question, answer) => {
    if (question.required) {
      if (!answer) return "This question is required";
      if (question.type === 'rating_grid' || question.type === 'numeric_grid') {
        if (!answer?.gridAnswers?.length) {
          return "Please provide answers for the grid";
        }
        
        // For required grids, ensure all cells are filled
        const totalCells = (question.settings?.gridRows?.length || 0) * 
                          (question.settings?.gridColumns?.length || 0);
        if (answer.gridAnswers.length < totalCells) {
          return "Please complete all grid entries";
        }
      }
      switch (question.type) {
        case "short_text":
        case "long_text":
          if (!answer.answerText?.trim()) return "Please provide an answer";
          break;

        case "single_choice":
          if (!answer.answerText) return "Please select an option";
          break;

        case "multiple_choice":
          if (!answer.selectedOptions?.length)
            return "Please select at least one option";
          break;

        case "rating":
        case "linear_scale":
          if (answer.scaleValue === null || answer.scaleValue === undefined) {
            return "Please provide a rating";
          }
          break;
      }
    }
    return null;
  };

  const handleAnswerChange = (answer) => {
    setResponses((prev) => ({
      ...prev,
      [survey.questions[currentStep]._id]: answer,
    }));
    setErrors((prev) => ({
      ...prev,
      [survey.questions[currentStep]._id]: null,
    }));
  };

  const handleNext = () => {
    const currentQuestion = survey.questions[currentStep];
    const answer = responses[currentQuestion._id];
    const error = validateAnswer(currentQuestion, answer);

    if (error) {
      setErrors((prev) => ({
        ...prev,
        [currentQuestion._id]: error,
      }));
      return;
    }

    if (currentStep < survey.questions.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);

      // Format responses for API
      const formattedAnswers = Object.entries(responses).map(
        ([questionId, answer]) => ({
          question: questionId,
          ...answer,
        })
      );

      await onSubmit({
        answers: formattedAnswers,
        metadata: {
          startedAt: startTimeRef.current,
          deviceInfo: navigator.userAgent,
        },
      });
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        submit: error.message,
      }));
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>
            Question {currentStep + 1} of {survey.questions.length}
          </span>
          <span>
            {Math.round(((currentStep + 1) / survey.questions.length) * 100)}%
            Complete
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <motion.div
            className="bg-blue-600 h-2 rounded-full"
            initial={{ width: 0 }}
            animate={{
              width: `${((currentStep + 1) / survey.questions.length) * 100}%`,
            }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Question Display */}
      <div className="mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {survey.questions[currentStep].questionText}
          {survey.questions[currentStep].required && (
            <span className="text-red-500 ml-1">*</span>
          )}
        </h3>

        <QuestionRenderer
          question={survey.questions[currentStep]}
          value={responses[survey.questions[currentStep]._id]}
          onChange={handleAnswerChange}
          error={errors[survey.questions[currentStep]._id]}
        />
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <button
          onClick={
            currentStep === 0
              ? onCancel
              : () => setCurrentStep((prev) => prev - 1)
          }
          className="px-4 py-2 text-gray-600 hover:text-gray-900 flex items-center"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          {currentStep === 0 ? "Exit" : "Previous"}
        </button>

        <button
          onClick={handleNext}
          disabled={submitLoading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg 
            hover:bg-blue-700 disabled:bg-gray-400 flex items-center"
        >
          {submitLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              {currentStep === survey.questions.length - 1 ? "Submit" : "Next"}
              <ChevronRight className="w-4 h-4 ml-1" />
            </>
          )}
        </button>
      </div>

      {errors.submit && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
          {errors.submit}
        </div>
      )}
    </div>
  );
};

// Main Component
const AlumniSurvey = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("deadline");
  const [submittedSurveys, setSubmittedSurveys] = useState(new Set());
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  useEffect(() => {
    fetchSurveys();
    fetchSubmittedSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/surveys/available-surveys`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch surveys");

      const data = await response.json();
      setSurveys(data);
    } catch (err) {
      console.error("Error fetching surveys:", err);
      setError("Failed to load surveys");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmittedSurveys = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/surveys/my-surveys`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch submissions");

      const data = await response.json();
      setSubmittedSurveys(
        new Set(data.map((submission) => submission.survey._id))
      );
    } catch (err) {
      console.error("Error fetching submissions:", err);
    }
  };

  const filteredSurveys = surveys
    .filter((survey) => {
      const matchesSearch =
        (survey.title?.toLowerCase() || "").includes(
          searchTerm.toLowerCase()
        ) ||
        (survey.description?.toLowerCase() || "").includes(
          searchTerm.toLowerCase()
        );

      const matchesFilter =
        filter === "all" ||
        (filter === "completed" && submittedSurveys.has(survey._id)) ||
        (filter === "active" &&
          !submittedSurveys.has(survey._id) &&
          new Date(survey.schedule.endDate) > new Date());

      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      switch (sortOrder) {
        case "deadline":
          return new Date(a.schedule.endDate) - new Date(b.schedule.endDate);
        case "recent":
          return new Date(b.createdAt) - new Date(a.createdAt);
        case "title":
          return (a.title || "").localeCompare(b.title || "");
        default:
          return 0;
      }
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  const handleStartSurvey = (survey) => {
    setSelectedSurvey(survey);
  };

  const handleSubmitSurvey = async (responseData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/surveys/surveys/${selectedSurvey._id}/respond`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(responseData)
        }
      );
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit survey');
      }
  
      // Show success animation
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
  
      // Update submitted surveys and reset state
      await fetchSubmittedSurveys();
      setSelectedSurvey(null);
      
    } catch (error) {
      throw error;
    }
  };
  return (
    <div className="min-h-screen bg-gray-50">
     <HeroInfoSection/>

      {/* Existing Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {selectedSurvey ? (
          <SurveyResponseForm
            survey={selectedSurvey}
            onSubmit={handleSubmitSurvey}
            onCancel={() => setSelectedSurvey(null)}
          />
        ) : (
          <div id="surveysection">
          
            <StatsSection surveys={surveys} submittedCount={submittedSurveys.size} />
            <FilterTabs activeFilter={filter} onFilterChange={setFilter} />
            <SearchAndSort
              searchTerm={searchTerm}
              onSearchChange={(e) => setSearchTerm(e.target.value)}
              sortOrder={sortOrder}
              onSortChange={(e) => setSortOrder(e.target.value)}
            />

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
                {error}
              </div>
            )}

            {/* Survey Grid */}
            {filteredSurveys.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredSurveys.map((survey) => (
                  <SurveyCard
                    key={survey._id}
                    survey={survey}
                    isSubmitted={submittedSurveys.has(survey._id)}
                    onStart={handleStartSurvey}
                  />
                ))}
              </div>
            ) : (
              <NoDataMessage
                message={
                  searchTerm
                    ? "No surveys match your search"
                    : filter === "completed"
                    ? "You haven't completed any surveys yet"
                    : "No surveys available at the moment"
                }
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlumniSurvey;